import React, { useEffect } from "react";
import AxisDiagramTABO from "./AxisDiagramTABO";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { WheelSelect, SPHERE_OPTIONS, CYL_OPTIONS, AXIS_OPTIONS, ACUITY_OPTIONS, ADD_OPTIONS } from "./RefrazioneAvanzataBlock";

/**
 * Prescrizione Occhiali — layout ottico professionale (SPH / CYL / AXIS / PRISMA / BASE + ADD SF).
 * Si deriva automaticamente dalla refrazione OD/OS (sfera/cilindro/asse/addizione) ma rimane editabile.
 * Visualizza diagramma TABO live per ciascun occhio.
 * Se vuota, NON appare nel PDF (logica server in _build_visit_pdf).
 * Bottone "Trasponi" per OD/OS: applica S' = S+C, C' = -C, A' = A ± 90 normalizzato 0-180.
 */

// Trasposizione del cilindro: S' = S + C ; C' = -C ; A' = (A ± 90) mod 180 (range 0-180, 0 -> 180)
export function transposeCylinder({ sph, cyl, axis }) {
  const parseNum = (s) => {
    if (s === null || s === undefined) return null;
    const t = String(s).trim().replace(",", ".").replace(/^\+/, "");
    if (t === "" || t === "-") return null;
    const n = parseFloat(t);
    return Number.isFinite(n) ? n : null;
  };
  const sN = parseNum(sph); const cN = parseNum(cyl); const aN = parseNum(axis);
  if (cN === null || cN === 0) return null; // nulla da trasporre
  const newS = (sN || 0) + cN;
  const newC = -cN;
  let newA = (aN || 0) + 90;
  newA = ((newA % 180) + 180) % 180; // normalizza 0..179
  if (newA === 0) newA = 180; // convenzione TABO: 180 = orizzontale
  const fmtD = (n) => (n >= 0 ? `+${n.toFixed(2)}` : `${n.toFixed(2)}`);
  return {
    sph: fmtD(newS),
    cyl: fmtD(newC),
    axis: String(Math.round(newA)),
  };
}

export default function PrescrizioneOcchialiBlock({ od, os, prescrizione, setPrescrizione, readOnly }) {
  // Mapping: dal modello refrazione (sfera/cilindro/asse/add) ai campi prescrizione (SPH/CYL/AXIS/ADD)
  useEffect(() => {
    if (readOnly) return;
    const newP = { ...(prescrizione || {}) };
    const fillFrom = (eyeKey, src) => {
      const cur = newP[eyeKey] || {};
      const updated = { ...cur };
      if (!updated.sph && src?.sfera) updated.sph = src.sfera;
      if (!updated.cyl && src?.cilindro) updated.cyl = src.cilindro;
      if (!updated.axis && src?.asse) updated.axis = src.asse;
      if (!updated.add && src?.add) updated.add = src.add;
      // retro-compat con i vecchi nomi
      if (!updated.sph && cur.sfera) updated.sph = cur.sfera;
      if (!updated.cyl && cur.cilindro) updated.cyl = cur.cilindro;
      if (!updated.axis && cur.asse) updated.axis = cur.asse;
      if (!updated.add && cur.addizione) updated.add = cur.addizione;
      return updated;
    };
    newP.OD = fillFrom("OD", od);
    newP.OS = fillFrom("OS", os);
    if (JSON.stringify(newP) !== JSON.stringify(prescrizione || {})) setPrescrizione(newP);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [od?.sfera, od?.cilindro, od?.asse, od?.add, os?.sfera, os?.cilindro, os?.asse, os?.add]);

  const p = prescrizione || {};
  const upd = (path, val) => {
    const next = JSON.parse(JSON.stringify(p));
    const keys = path.split(".");
    let cur = next;
    for (let i = 0; i < keys.length - 1; i++) {
      cur[keys[i]] = cur[keys[i]] || {};
      cur = cur[keys[i]];
    }
    cur[keys[keys.length - 1]] = val;
    setPrescrizione(next);
  };

  const cell = (eye, field, ph, w = "w-full") => (
    <input
      type="text"
      value={(p[eye] || {})[field] || ""}
      onChange={(e) => upd(`${eye}.${field}`, e.target.value)}
      disabled={readOnly}
      placeholder={ph}
      data-testid={`presc-${eye}-${field}`}
      className={`${w} px-2 py-1.5 rounded border border-[#E2E8F0] focus:outline-none focus:border-brand-azure text-sm disabled:bg-slate-50 text-center font-mono`}
    />
  );

  // Valori legacy fuori scala restano selezionabili/visibili
  const withVal = (opts, v) => (v && !opts.includes(v) ? [v, ...opts] : opts);

  const COLS = [
    { k: "sph", label: "SPH", options: SPHERE_OPTIONS },
    { k: "cyl", label: "CYL", options: CYL_OPTIONS },
    { k: "axis", label: "AXIS", options: AXIS_OPTIONS },
    { k: "visus", label: "Visus corr.", options: ACUITY_OPTIONS },
  ];

  const transpose = (eye) => {
    const cur = p[eye] || {};
    const t = transposeCylinder({ sph: cur.sph, cyl: cur.cyl, axis: cur.axis });
    if (!t) { toast.info(`${eye}: nessun cilindro da trasporre`); return; }
    const next = JSON.parse(JSON.stringify(p));
    next[eye] = { ...(next[eye] || {}), sph: t.sph, cyl: t.cyl, axis: t.axis };
    setPrescrizione(next);
    toast.success(`${eye} trasposto: ${t.sph} ${t.cyl} asse ${t.axis}°`);
  };

  // Retro-compat: eventuali dati salvati nel blocco (rimosso) "nuova prescrizione L/I/V" (avanzata)
  const avLegacy = ["lontano", "intermedio", "vicino"].flatMap((dist) =>
    ["OD", "OS"].flatMap((eye) => {
      const c = ((p.avanzata || {})[dist] || {})[eye] || {};
      const vals = ["sph", "cyl", "axis", "add", "visus"].filter((k) => c[k]);
      return vals.length ? [`${dist} ${eye}: ${vals.map((k) => `${k} ${c[k]}`).join(", ")}`] : [];
    })
  );

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 mb-4" data-testid="prescrizione-occhiali-block">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-1 h-5 bg-brand-orange rounded-full"></span>
        <h3 className="font-extrabold text-brand-navy text-base">Prescrizione Occhiali</h3>
        <span className="text-[10px] uppercase tracking-wider bg-brand-orange/10 text-brand-orange px-2 py-0.5 rounded font-bold">Notazione assi: TABO</span>
      </div>
      {/* Griglia column-major: DOM = per ogni colonna OD poi OS → TAB orizzontale (OD SPH → OS SPH → OD CYL…) */}
      <div className="overflow-x-auto">
        <div className="grid gap-x-1.5 gap-y-1.5 items-center min-w-[760px] text-sm"
          style={{ gridTemplateColumns: "44px repeat(4, minmax(84px, 1fr)) minmax(64px, 0.7fr) minmax(84px, 0.9fr) 116px 96px" }}>
          <div className="text-left text-slate-500 text-[10px] uppercase tracking-wider font-bold py-1" style={{ gridRow: 1, gridColumn: 1 }}>Occhio</div>
          {COLS.map((c, i) => (
            <div key={`h-${c.k}`} className="text-center text-slate-500 text-[10px] uppercase tracking-wider font-bold" style={{ gridRow: 1, gridColumn: i + 2 }}>{c.label}</div>
          ))}
          <div className="text-center text-slate-500 text-[10px] uppercase tracking-wider font-bold" style={{ gridRow: 1, gridColumn: 6 }}>PRISMA</div>
          <div className="text-center text-slate-500 text-[10px] uppercase tracking-wider font-bold" style={{ gridRow: 1, gridColumn: 7 }}>BASE</div>
          <div className="text-center text-slate-500 text-[10px] uppercase tracking-wider font-bold" style={{ gridRow: 1, gridColumn: 8 }}>Asse TABO</div>
          <div className="text-center text-slate-500 text-[10px] uppercase tracking-wider font-bold" style={{ gridRow: 1, gridColumn: 9 }}>Trasposizione</div>

          {["OD", "OS"].map((eye, r) => (
            <div key={`lbl-${eye}`} className="font-extrabold text-brand-navy py-1" style={{ gridRow: r + 2, gridColumn: 1 }}>{eye}</div>
          ))}
          {/* SPH/CYL/AXIS/VISUS con rotellina step 0.25 — DOM column-major per TAB orizzontale */}
          {COLS.map((c, i) =>
            ["OD", "OS"].map((eye, r) => (
              <div key={`${c.k}-${eye}`} style={{ gridRow: r + 2, gridColumn: i + 2 }}>
                <WheelSelect value={(p[eye] || {})[c.k] || ""} options={withVal(c.options, (p[eye] || {})[c.k])}
                  disabled={readOnly} testid={`presc-${eye}-${c.k}`}
                  onChange={(val) => upd(`${eye}.${c.k}`, val)} />
              </div>
            ))
          )}
          {/* PRISMA e BASE (testo libero) */}
          {["prisma", "base"].map((fk, ci) =>
            ["OD", "OS"].map((eye, r) => (
              <div key={`${fk}-${eye}`} style={{ gridRow: r + 2, gridColumn: 6 + ci }}>
                {cell(eye, fk, fk === "prisma" ? "Δ" : "es. BI/BE")}
              </div>
            ))
          )}
          {/* Diagramma TABO + Trasponi (fuori dal tab order) */}
          {["OD", "OS"].map((eye, r) => (
            <div key={`tabo-${eye}`} className="px-1" style={{ gridRow: r + 2, gridColumn: 8 }}>
              <AxisDiagramTABO axis={(p[eye] || {}).axis} label={eye} />
            </div>
          ))}
          {["OD", "OS"].map((eye, r) => (
            <div key={`tr-${eye}`} className="text-center" style={{ gridRow: r + 2, gridColumn: 9 }}>
              <button
                type="button"
                tabIndex={-1}
                onClick={() => transpose(eye)}
                disabled={readOnly}
                data-testid={`presc-transpose-${eye}`}
                title={`Trasponi cilindro ${eye}: S'=S+C, C'=-C, A'=A±90`}
                className="inline-flex items-center gap-1 px-2 py-1 rounded border border-brand-azure/40 text-brand-azure hover:bg-brand-azure hover:text-white text-[10px] font-bold uppercase tracking-wider disabled:opacity-40 transition"
              >
                <RefreshCw size={10} /> Trasponi
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Due Add/SF distinti: intermedio e vicino (il vicino resta compatibile con add_sf usato dal PDF) */}
      <div className="mt-3 flex flex-wrap items-center gap-5">
        <div className="flex items-center gap-2">
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Add/SF intermedio</label>
          <div className="w-24">
            <WheelSelect value={p.add_sf_intermedio || ""} options={withVal(ADD_OPTIONS, p.add_sf_intermedio)}
              disabled={readOnly} testid="presc-add-sf-intermedio"
              onChange={(val) => upd("add_sf_intermedio", val)} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Add/SF vicino</label>
          <div className="w-24">
            <WheelSelect value={p.add_sf_vicino || p.add_sf || ""} options={withVal(ADD_OPTIONS, p.add_sf_vicino || p.add_sf)}
              disabled={readOnly} testid="presc-add-sf"
              onChange={(val) => {
                // scrive anche add_sf legacy (usato dal PDF referto) — retro-compatibile
                const next = JSON.parse(JSON.stringify(p));
                next.add_sf_vicino = val;
                next.add_sf = val;
                setPrescrizione(next);
              }} />
          </div>
        </div>
      </div>

      {avLegacy.length > 0 && (
        <div className="mt-3 text-[10px] text-slate-600 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="presc-avanzata-legacy">
          <b>Dati prescrizione precedenti (blocco Lontano/Intermedio/Vicino, sola lettura):</b>
          {avLegacy.map((l, i) => <div key={i}>{l}</div>)}
        </div>
      )}

      <div className="mt-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Distanza interpupillare (PD)</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <input type="text" value={(p.pd || {}).totale || ""} onChange={(e) => upd("pd.totale", e.target.value)} disabled={readOnly} placeholder="Totale (mm)" data-testid="presc-pd-totale" className="px-2 py-1.5 rounded border border-[#E2E8F0] text-xs disabled:bg-slate-50" />
          <input type="text" value={(p.pd || {}).monoc_dx || ""} onChange={(e) => upd("pd.monoc_dx", e.target.value)} disabled={readOnly} placeholder="Monoc. DX" data-testid="presc-pd-dx" className="px-2 py-1.5 rounded border border-[#E2E8F0] text-xs disabled:bg-slate-50" />
          <input type="text" value={(p.pd || {}).monoc_sx || ""} onChange={(e) => upd("pd.monoc_sx", e.target.value)} disabled={readOnly} placeholder="Monoc. SX" data-testid="presc-pd-sx" className="px-2 py-1.5 rounded border border-[#E2E8F0] text-xs disabled:bg-slate-50" />
          <input type="text" value={(p.pd || {}).lontano || ""} onChange={(e) => upd("pd.lontano", e.target.value)} disabled={readOnly} placeholder="Lontano" data-testid="presc-pd-lontano" className="px-2 py-1.5 rounded border border-[#E2E8F0] text-xs disabled:bg-slate-50" />
          <input type="text" value={(p.pd || {}).vicino || ""} onChange={(e) => upd("pd.vicino", e.target.value)} disabled={readOnly} placeholder="Vicino" data-testid="presc-pd-vicino" className="px-2 py-1.5 rounded border border-[#E2E8F0] text-xs disabled:bg-slate-50" />
        </div>
      </div>

      <textarea
        rows={2}
        value={p.note || ""}
        onChange={(e) => upd("note", e.target.value)}
        disabled={readOnly}
        placeholder="Note (lenti progressive, antiriflesso, fotocromatiche, occhi distinti…)"
        data-testid="presc-note"
        style={{ lineHeight: "1.55" }}
        className="w-full mt-3 px-2 py-1.5 rounded border border-[#E2E8F0] focus:outline-none focus:border-brand-azure text-sm disabled:bg-slate-50 resize-none leading-relaxed"
      />
    </div>
  );
}
